import { FieldPath, UseFormClearErrors, UseFormSetValue } from 'react-hook-form';
import { ReverseVestingSettings } from 'shared';

import { fromBackendDateFormat } from '../../../../utils/from-backend-date-format.util';
import { toBackendDateFormat } from '../../../../utils/to-backend-date-format.util';
import { toNumber } from '../../../../utils/to-number.util';
import { ReverseVestingFieldPath } from '../components/reverse-vesting-fields';
import {
  FormSchema,
  ReverseVestingFormData,
  reverseVestingRowSchema,
} from '../event-form.validation';

/**
 * Single source of truth for both directions of the reverse-vesting mapping.
 *
 * Every event edit is a destructive delete-and-recreate on the backend, so a schedule only
 * survives an edit because the form round-trips it. Both helpers are used at every mapper call
 * site precisely so a missed one cannot silently wipe a stakeholder's configuration.
 */
export const mapReverseVestingFormToDto = (
  reverseVesting: ReverseVestingFormData | undefined,
): ReverseVestingSettings | undefined => {
  if (!reverseVesting?.enabled) return undefined;

  const { cliffMonths, durationMonths, frequencyMonths, shares, startDate, vestingOn } =
    reverseVesting;

  // The form schema already rejects an enabled block with any of these missing; the guard keeps
  // the mapper total rather than asserting non-null.
  if (
    shares === undefined ||
    startDate === undefined ||
    vestingOn === undefined ||
    durationMonths === undefined ||
    frequencyMonths === undefined
  ) {
    return undefined;
  }

  return {
    sharesCount: Number(shares),
    startDate: toBackendDateFormat(startDate),
    vestingOn,
    durationMonths: Number(durationMonths),
    frequencyMonths: Number(frequencyMonths),
    ...(cliffMonths === undefined ? {} : { cliffMonths: Number(cliffMonths) }),
  };
};

export const mapReverseVestingToForm = (
  reverseVesting: ReverseVestingSettings | undefined,
): ReverseVestingFormData | undefined => {
  if (!reverseVesting) return undefined;

  return {
    enabled: true,
    shares: toNumber(reverseVesting.sharesCount),
    // jsonb hands dates back as strings; the form works in Date. Inverting `toBackendDateFormat`
    // rather than parsing keeps the anchor on the day it was saved — a bare `new Date` would walk
    // it back one day per open-and-save wherever local time is behind UTC.
    startDate: fromBackendDateFormat(reverseVesting.startDate),
    vestingOn: reverseVesting.vestingOn,
    durationMonths: toNumber(reverseVesting.durationMonths),
    frequencyMonths: toNumber(reverseVesting.frequencyMonths),
    // The backend still accepts a 0 cliff, which the form now rejects. They mean the same schedule,
    // so normalise on read rather than dead-ending the edit form on data the user cannot fix.
    cliffMonths: reverseVesting.cliffMonths ? toNumber(reverseVesting.cliffMonths) : undefined,
  };
};

/** An enabled reverse-vesting block that would not pass the row schema; off counts as complete. */
export const isReverseVestingIncomplete = (
  row: { reverseVesting?: ReverseVestingFormData; shares?: number | string } | undefined,
): boolean =>
  Boolean(row?.reverseVesting?.enabled) && !reverseVestingRowSchema.safeParse(row).success;

/**
 * A repurchase schedule belongs to the stakeholder it was agreed with, so re-pointing an issuance
 * row at someone else drops it rather than silently transferring it — the shares, dates and cliff
 * were negotiated with the previous holder.
 *
 * Driven by the combobox's selection callback rather than by watching the field, so hydrating an
 * existing event (which also writes the stakeholder) never counts as a change.
 */
export const resetReverseVestingOnStakeholderChange = ({
  clearErrors,
  currentStakeholderId,
  namePrefix,
  nextStakeholderId,
  setFormData,
}: {
  clearErrors: UseFormClearErrors<FormSchema>;
  currentStakeholderId: string | undefined;
  namePrefix: ReverseVestingFieldPath;
  nextStakeholderId: string;
  setFormData: UseFormSetValue<FormSchema>;
}): void => {
  if (!currentStakeholderId || currentStakeholderId === nextStakeholderId) return;

  setFormData(namePrefix, undefined);
  clearErrors(namePrefix);
};

/**
 * The repurchase count is a carve-out of the issuance, so editing "No of shares" rewrites it
 * instead of leaving a figure the issuance can no longer cover — the panel otherwise reads
 * "1,231 shares / 10,258.33%" against an issuance of 12, and the row only fails at submit.
 *
 * A deliberate subset survives the edit while it still fits: only a count that covered the whole
 * issuance, or one that no longer fits inside it, follows the new figure. Clearing the issuance
 * empties the field rather than pinning it to 0, which would read as a valid schedule of nothing.
 *
 * Driven by the input's change callback rather than by watching the field, for the same reason as
 * the stakeholder reset above: hydrating an existing event writes `shares` too, and that must not
 * count as a user edit.
 */
export const resetReverseVestingSharesOnIssuedSharesChange = ({
  clearErrors,
  currentIssuedShares,
  namePrefix,
  nextIssuedShares,
  reverseVesting,
  setFormData,
}: {
  clearErrors: UseFormClearErrors<FormSchema>;
  currentIssuedShares: number;
  namePrefix: ReverseVestingFieldPath;
  nextIssuedShares: number;
  reverseVesting: ReverseVestingFormData | undefined;
  setFormData: UseFormSetValue<FormSchema>;
}): void => {
  if (!reverseVesting?.enabled) return;
  if (nextIssuedShares === currentIssuedShares) return;

  const reverseVestedShares =
    reverseVesting.shares === undefined ? undefined : toNumber(reverseVesting.shares);

  const coversWholeIssuance =
    reverseVestedShares === undefined || reverseVestedShares === currentIssuedShares;

  if (!coversWholeIssuance && reverseVestedShares <= nextIssuedShares) return;

  const sharesPath = `${namePrefix}.shares` as FieldPath<FormSchema>;

  setFormData(sharesPath, nextIssuedShares || undefined);
  clearErrors(sharesPath);
};
